import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router';

import useCar from '~/data/hooks/useCar';
import {
  knowledgeTargetLabel,
  type KnowledgeTargetProperty,
  useKnowledgeTargets,
} from '~/data/hooks/useKnowledgeTargets';

export interface PageContext {
  route: string;
  entityType: 'property' | 'car' | null;
  propertyId?: string;
  carId?: string;
  label?: string;
}

const PROPERTY_ROUTE = '/properties/:id';
const CAR_ROUTE = '/compliance/:carId';

export function derivePageContext(
  pathname: string,
  properties: KnowledgeTargetProperty[],
  carRecord?: { name: string; propertyId?: string; propertyName?: string },
): PageContext {
  const propertyMatch = matchPath(PROPERTY_ROUTE, pathname);
  if (propertyMatch?.params.id) {
    const propertyId = propertyMatch.params.id;
    return {
      route: pathname,
      entityType: 'property',
      propertyId,
      label: knowledgeTargetLabel(properties, { propertyId }),
    };
  }

  const carMatch = matchPath(CAR_ROUTE, pathname);
  if (carMatch?.params.carId) {
    const carId = carMatch.params.carId;
    const listed = properties.find((p) => p.cars.some((c) => c.id === carId));
    if (listed) {
      return {
        route: pathname,
        entityType: 'car',
        carId,
        propertyId: listed.id,
        label: knowledgeTargetLabel(properties, { propertyId: listed.id, carId }),
      };
    }
    const propertyId = carRecord?.propertyId;
    return {
      route: pathname,
      entityType: 'car',
      carId,
      ...(propertyId
        ? {
            propertyId,
            label: carRecord?.propertyName
              ? `${carRecord.name} · ${carRecord.propertyName}`
              : carRecord?.name,
          }
        : {}),
    };
  }

  return { route: pathname, entityType: null };
}

export interface ChatPageContext {
  entityType: 'property' | 'car';
  propertyId?: string;
  carId?: string;
}

/** Never the label: no client free text reaches the assistant's prompt. */
export function toChatPageContext(context: PageContext | null): ChatPageContext | null {
  if (!context?.entityType) return null;
  const { entityType, propertyId, carId } = context;
  if (entityType === 'car') {
    return carId ? { entityType, carId, ...(propertyId ? { propertyId } : {}) } : null;
  }
  return propertyId ? { entityType, propertyId } : null;
}

export function usePageContext(): PageContext {
  const { pathname } = useLocation();
  const { properties } = useKnowledgeTargets();
  const carId = matchPath(CAR_ROUTE, pathname)?.params.carId;
  const { car } = useCar(carId);
  const carName = car?.name;
  const carPropertyId = car?.property?.id;
  const carPropertyName = car?.property?.name;

  return useMemo(
    () =>
      derivePageContext(
        pathname,
        properties,
        carName
          ? { name: carName, propertyId: carPropertyId, propertyName: carPropertyName }
          : undefined,
      ),
    [pathname, properties, carName, carPropertyId, carPropertyName],
  );
}
